import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { any, string, func, array, bool } from 'prop-types';
import { Input, Button, Spin, Modal, Checkbox, message } from 'antd';

import Layout from 'components/layout';
import Breadcrumb from 'components/breadcrumb';
import Map from 'components/map';
import { InStoreSVG } from 'components/iconsSVG/inStore';
import { VirtualAppointmentSVG } from 'components/iconsSVG/virtualAppointment';
import { CloseSVG, CloseMdSVG } from 'components/iconsSVG/close';
import { LocationSVG } from 'components/iconsSVG/location';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import StoresGrid, { Scheduler, StoreTypeLabel } from 'components/storesGrid';
import { AutoComplete } from 'components/base';
import { i18nContext, useI18n } from 'context/i18n';
import { getLang, getMarket, getIsMobile, debounce } from 'utils/helpers';
import { handleNominatimResult, checkPostalCode } from 'utils/storeUtils';
import { getRegionsAndCities } from 'actions/general';
import { getStoresByCountry } from 'actions/shops';
import { normalizeUrl, normalizeString, removeStrangeCharacters, hasNonAlphaCharacters } from 'utils/StringUtils';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { StoreStatus } from 'components/store-status';

import { COUNTRIES_NO_PERSONAL_SHOPPER, STORE_TYPES } from 'utils/constants/stores';

import AppointmentModal from 'components/appointmentModal';
import { geoReverse, geoSearch } from 'actions/geo-search';

import styles from './camper-stores.module.css';

const baseUrl = process.env.INTEGRA_DOMAIN;

const sortStoresByTypology = (stores) => {
  const tipologySort = {
    'TIENDA CAMPER': 1,
    RECAMPER: 2,
    FRANCHISE: 3,
  };

  return stores.sort((a, b) => {
    const aOrder = tipologySort[a.tipologia] || Infinity;
    const bOrder = tipologySort[b.tipologia] || Infinity;
    return aOrder - bOrder;
  });
};

type FilterPanelProps = {
  filterByOpen: boolean;
  setFilterByOpen: (value: boolean) => void;
  filterBy: string[];
  onClickFilter: (value: string) => void;
};
function FilterPanel({ filterByOpen, setFilterByOpen, filterBy, onClickFilter }: FilterPanelProps) {
  const { t } = useI18n();
  return (
    <div className={!filterByOpen ? styles.filterBy : `${styles.filterBy} ${styles.open}`}>
      <span className={!filterByOpen ? styles.filterByLabel : ''} onClick={() => setFilterByOpen(!filterByOpen)}>
        {t('tiendas.locator', 'filter.by', 'Filter by')}{' '}
        {!filterByOpen ?
          <ChevronSVG />
        : <CloseMdSVG width={16} height={16} />}
      </span>
      {filterByOpen && (
        <>
          <p className={styles.collection}>{t('tiendas.locator', 'collections', 'Collections')}</p>
          <ul>
            <li>
              <Checkbox checked={filterBy.includes('featWomen')} onChange={() => onClickFilter('featWomen')}>
                {t('tiendas.locator', 'women.collection', 'Women Collection')}
              </Checkbox>
            </li>
            <li>
              <Checkbox checked={filterBy.includes('featMen')} onChange={() => onClickFilter('featMen')}>
                {t('tiendas.locator', 'men.collection', 'Men Collection')}
              </Checkbox>
            </li>
            <li>
              <Checkbox checked={filterBy.includes('featKids')} onChange={() => onClickFilter('featKids')}>
                {t('tiendas.locator', 'kids.collection', 'Kids Collection')}
              </Checkbox>
            </li>
            <li>
              <Checkbox checked={filterBy.includes('featBags')} onChange={() => onClickFilter('featBags')}>
                {t('tiendas.locator', 'bag.collection', 'Bags & Accesories Collection')}
              </Checkbox>
            </li>
            <li>
              <Checkbox checked={filterBy.includes('featCamperlab')} onChange={() => onClickFilter('featCamperlab')}>
                {t('tiendas.locator', 'camperlab.collection', 'Camperlab Collection')}
              </Checkbox>
            </li>
          </ul>
          <p className={styles.collection}>{t('tiendas.locator', 'services', 'Services')}</p>
          <ul>
            <li>
              <Checkbox checked={filterBy.includes('featTakeback')} onChange={() => onClickFilter('featTakeback')}>
                {t('tiendas.locator', 'takeback.service', 'Take Back')}
              </Checkbox>
            </li>
          </ul>
          <p className={styles.collection}>{t('tiendas.locator', 'store.type', 'Store type')}</p>
          <ul>
            {STORE_TYPES.map((store) => (
              <li key={store.type}>
                <Checkbox checked={filterBy.includes(`tipologia: ${store.type}`)} onChange={() => onClickFilter(`tipologia: ${store.type}`)}>
                  {t(...store.label)}
                </Checkbox>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

type CamperStoresProps = {
  defaultProps: any;
  preloadedStores: any[];
  preloadedSearch: string;
  preloadedCities: any[];
  allStoresByCountry: any[];
  allStoresByCity: any[];
  countryProp: string;
  placeProp: string;
  urlCanonical: string;
  preloadedStoresByTypology: any[];
  preloadedFilterBy: any[];
  firstStep: boolean;
  shoppingLinks: any[];
};

export function CamperStores({
  defaultProps,
  preloadedStores,
  preloadedSearch,
  preloadedCities,
  countryProp,
  placeProp,
  urlCanonical,
  preloadedStoresByTypology,
  preloadedFilterBy,
  firstStep,
  shoppingLinks,
  allStoresByCountry,
  allStoresByCity,
}: CamperStoresProps) {
  const { t, countries, locale } = useContext(i18nContext);
  const isMobile = getIsMobile('767.5px');

  const router = useRouter(); // para navegadores que no tienen history en window

  const changeRoute = (url) => {
    if (window?.history?.pushState) {
      return window.history.pushState({}, '', url);
    }

    return router.push(url, undefined, {
      shallow: true,
    });
  };

  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState(preloadedSearch);
  const [stores, setStores] = useState(preloadedStoresByTypology || preloadedStores);
  const [selectedStore, setSelectedStore] = useState(null);
  const [filterByOpen, setFilterByOpen] = useState(false);
  const [isShowingMap, setShowingMap] = useState(true);
  const [showingFullMap, setShowingFullMap] = useState(firstStep);
  const [centeredStore, setCenteredStore] = useState(null);
  const [currentPlace, setCurrentPlace] = useState(placeProp || countryProp);
  const [currentCountry, setCurrentCountry] = useState(countryProp || null);
  const [countryCode, setCountryCode] = useState(countryProp === '日本' ? 'JP' : null);
  const [isCountry, setIsCountry] = useState(preloadedCities === null ? true : preloadedCities?.length > 0); // si preloaded cities es null es un país que recibe datos malos del api
  const [cities, setCities] = useState(preloadedCities === null ? [] : preloadedCities);
  const lang = getLang(locale).toLowerCase();
  const market = getMarket(locale);
  const urlCountry = getMarket(locale).toLowerCase();
  const searchFromOwnGeo = useRef(false);
  const searchedOne = useRef(false);
  const userHasWritten = useRef(false);
  const [filteredCity, setFilteredCity] = useState(null);
  const [coloredStore, setColoredStore] = useState(null);

  // for not found form
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [filteredCities, setFilteredCities] = useState(null);
  const [loadingCities, setLoadingCities] = useState(false);
  const notFoundCountryRef = useRef(null);
  const notFoundCityRef = useRef(null);
  const isSearchingFromNotFound = useRef(false);

  const [countryHasBadData, setCountryHasBadData] = useState(false);
  const [filterBy, setFilterBy] = useState(preloadedFilterBy);
  const [availableStores, setAvailableStores] = useState(preloadedStores);
  const [appointmentVisible, setAppointmentVisible] = useState(false);
  const [detectedCountryName, setCountryName] = useState(null);
  const [storesByCountry, setStoresByCountry] = useState(null);

  const onClickFilter = (filterName) => {
    const isTipologyFilter = filterName.includes('tipologia: ');
    const newFilterBy = JSON.parse(JSON.stringify(filterBy));
    const indexOfFilter = filterBy.indexOf(filterName);
    if (indexOfFilter === -1) {
      newFilterBy.push(filterName);
      dataLayerHandleEvent({
        event: 'filter_category',
        event_category: 'filters',
        event_detail_1: 'stores',
        event_detail_2:
          isTipologyFilter ? `${filterName.replace('tipologia: ', '').toLowerCase()} store` : `${filterName.replace('feat', '').toLowerCase()} collection`,
      });
    } else {
      newFilterBy.splice(indexOfFilter, 1);
    }
    const storesToFilter = JSON.parse(JSON.stringify(availableStores));
    let storesByTypology = null;
    const filterByTypology = newFilterBy.filter((filterVal) => filterVal.includes('tipologia: '));
    if (filterByTypology.length) {
      storesByTypology = storesToFilter.filter((store) => {
        let applies = false;
        // eslint-disable-next-line no-restricted-syntax
        for (const filterType of filterByTypology) {
          applies = store.tipologia === filterType.replace('tipologia: ', '');
          if (applies) {
            return applies;
          }
        }
        return applies;
      });
    }
    const newStoresToFilter = storesByTypology || storesToFilter;
    const newFilteredStores = newStoresToFilter.filter((store) => {
      let applies = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const filterType of newFilterBy) {
        if (filterType.includes('tipologia: ')) {
          applies = true;
        } else {
          applies = store[filterType] === true;
        }
      }
      return applies;
    });
    const appliedFiltersHasStores = newFilteredStores.length > 0;
    if (newFilterBy.length && !appliedFiltersHasStores) {
      // const index = newFilterBy.indexOf(filterName);
      // newFilterBy.splice(index, 1);
      message.open({
        type: 'info',
        content: t('tiendas.locator', 'notificacion.filtro.sin.tiendas', 'No hay resultados de tiendas para ese filtro'),
        className: styles.message,
      });
    }
    const storesToSet = appliedFiltersHasStores ? newFilteredStores : stores;
    setFilterBy(newFilterBy);
    setStores(newFilterBy.length === 0 ? storesToFilter : storesToSet);
    setCenteredStore(null);
    setColoredStore(null);
  };

  const restart = (partial = false) => {
    if (!partial) {
      setSearchString('');
      searchedOne.current = false;
    }

    setStores([]);
    setIsCountry(false);
    setShowingMap(true);
    setCenteredStore(null);
    setColoredStore(null);
    setFilterByOpen(false);
    setCurrentPlace(null);
    setCurrentCountry(null);
    setCountryCode(null);
    changeRoute(`/${locale}/shops`);
  };

  const handleNominatimSuccess = async (match, newStores, newCities) => {
    const { address, namedetails } = match;
    const { city, country_code, administrative, country, province, county, state } = address;

    const newIsCountry = country_code && !administrative && !state && !county && !province && !city;

    let localizedName =
      searchFromOwnGeo.current ?
        city || province || county || state || administrative
      : namedetails[`name:${lang}`] || namedetails[`name:${urlCountry}`] || namedetails[`name:${country_code}`] || namedetails.name;

    if (localizedName === undefined) {
      localizedName = newIsCountry ? country : administrative || state || county || province || city;
    }

    setIsCountry(newIsCountry);
    setCurrentPlace(localizedName);
    setSelectedCountry(null);
    setSelectedCity(null);
    setFilteredCities(null);
    const getEnglishCountryName = new Intl.DisplayNames(['en'], { type: 'region' });
    const newCountryCode = address?.country_code || '';
    const englishCountryName = getEnglishCountryName.of(newCountryCode.toUpperCase()).toLocaleLowerCase();
    setCountryCode(newCountryCode.toUpperCase());
    if (newIsCountry) {
      changeRoute(`/${locale}/shops/${localizedName.replace(/ñ/g, 'n').toLowerCase()}`);
      setCurrentCountry(localizedName);
      dataLayerHandleEvent({
        event: `store_search_${searchFromOwnGeo.current ? 'location' : 'form'}`,
        event_category: 'store',
        event_detail_1: englishCountryName,
        event_detail_2: '',
      });
    } else {
      let countryName = null;
      if (isSearchingFromNotFound.current && selectedCountry?.value) {
        countryName = selectedCountry.value;
        isSearchingFromNotFound.current = false;
      } else {
        const resultForCountry = await geoSearch({ query: { q: country, addressdetails: '1', namedetails: '1' } });

        const [firstMatchForCountry] = resultForCountry;

        if (firstMatchForCountry) {
          const { namedetails: countryNameDetails } = firstMatchForCountry;
          countryName = countryNameDetails[`name:${lang}`] || countryNameDetails.name;
        }
      }
      const cityNameEnglish = namedetails['name:en'] || namedetails?.name || '';
      dataLayerHandleEvent({
        event: `store_search_${searchFromOwnGeo.current ? 'location' : 'form'}`,
        event_category: 'store',
        event_detail_1: englishCountryName,
        event_detail_2: normalizeString(localizedName || cityNameEnglish).toLowerCase(),
      });
      changeRoute(`/${locale}/shops/${countryName.replace(/ñ/g, 'n').toLowerCase()}/${localizedName.toLowerCase()}`);
      setCurrentCountry(countryName);
    }

    if (searchString === '') {
      // comes from first screen
      setSearchString(localizedName);
    }

    setStores(newStores);
    setAvailableStores(newStores);
    // setCenteredStore([ lat, lon ]);

    if (newCities) {
      setCities(newCities);
    }
    setShowingFullMap(false);
    setLoading(false);
    setShowingMap(true);
  };

  const handleNominatimFailure = (mode, newRoute = true) => {
    if (mode === 'restart') {
      restart(true);
    }

    setStores([]);

    if (newRoute) {
      changeRoute(`/${locale}/shops`);
    }

    setLoading(false);
  };

  const handleGetLocation = () => {
    if ('geolocation' in navigator) {
      setLoading(true);
      navigator?.geolocation?.getCurrentPosition(
        async ({ coords }) => {
          const response = await geoReverse({
            lat: String(coords.latitude),
            lon: String(coords.longitude),
            language: lang,
          });
          searchFromOwnGeo.current = true;
          const { address } = response as any;
          const { city, administrative, country, province, county, state } = address;

          setSearchString(administrative || state || county || province || city || country);
          handleNominatimResult(locale, [response], handleNominatimSuccess, handleNominatimFailure);
        },
        (err) => {
          setLoading(false);
          console.error(err);
        },
      );
    }
  };

  const handleSearch = async (searchStr) => {
    if (searchStr !== '') {
      setLoading(true);
      setFilterBy([]);
      setFilterByOpen(false);
      setFilteredCity(null);
      setCenteredStore(null);
      setColoredStore(null);
      setShowingFullMap(false);
      let resultByCountry = null;
      try {
        resultByCountry = await getStoresByCountry(null, locale, searchStr, false);
      } catch (error) {
        console.error('error', error);
      }
      if (resultByCountry && 'stores' in resultByCountry && resultByCountry.stores !== undefined) {
        const { stores: newStores, cities: newCities } = resultByCountry;
        if (newCities === undefined) {
          handleNominatimFailure(false, false);
          setCountryHasBadData(true);
        } else {
          setIsCountry(true);
          setCurrentPlace(searchStr);
          changeRoute(`/${locale}/shops/${searchStr.replace(/ñ/g, 'n').toLowerCase()}`);
          setCurrentCountry(searchStr);
          setStores(newStores.filter((store) => store.latitude && store.longitude));
          setAvailableStores(newStores.filter((store) => store.latitude && store.longitude));
          setCities(newCities);
          setLoading(false);
          setShowingMap(true);
        }
      } else {
        const searchParams: any = {
          q: searchStr,
          addressdetails: '1',
          namedetails: '1',
          limit: '6',
        };

        const isPostalCode = checkPostalCode(locale, searchStr);

        if (isPostalCode) {
          delete searchParams.q;
          searchParams.postalcode = searchStr;
          searchParams.country = urlCountry.toLowerCase();
        }

        const res = await geoSearch({ query: searchParams, format: 'json' });
        handleNominatimResult(locale, res, handleNominatimSuccess, handleNominatimFailure);
      }
    }
  };

  const handleSearchChange = async (ev) => {
    const { target } = ev;
    const { value } = target;
    let valueToSet = value;
    if (userHasWritten.current === false) {
      userHasWritten.current = true;
      valueToSet = valueToSet.replace(searchString, '');
    }
    searchFromOwnGeo.current = false;
    setSearchString(valueToSet);
  };

  useEffect(() => {
    if ((placeProp || countryProp) && preloadedSearch && preloadedStores && preloadedStores.length) {
      setShowingMap(true);
    }
    const urlParams = new URLSearchParams(window.location.search);
    const storeId = urlParams.get('store');
    if (storeId) {
      setShowingFullMap(false);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const storeId = urlParams.get('store');
    const storeElement = document.querySelector(`a[href*="${storeId}"]`);
    if (!showingFullMap && storeElement) {
      urlParams.delete('store');
      if (window.history.replaceState) {
        const querystring = urlParams.toString().length > 0 ? `?${urlParams.toString()}` : '';
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${querystring}${window.location.hash}`;
        window.history.replaceState(null, '', newUrl);
      }
      storeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showingFullMap]);

  useEffect(() => {
    if (preloadedStoresByTypology === null) {
      setStores(preloadedStores);
    }
    setAvailableStores(preloadedStores);
  }, [preloadedStores]);

  useEffect(() => {
    if (stores !== preloadedStores && stores?.length === 0) {
      changeRoute(`/${locale}/shops`);
      setCurrentCountry(null);
      setCurrentPlace(null);
    }
  }, [stores]);

  useEffect(() => {
    setSearchString(preloadedSearch);
  }, [preloadedSearch]);

  useEffect(() => {
    if (preloadedCities === null) {
      // inconsitent data coming from api -> country with no stores || no cities
      searchedOne.current = true;
      handleNominatimFailure(false, false);
      setCountryHasBadData(true);
    } else {
      setCities(preloadedCities);
      setIsCountry(preloadedCities.length > 0);
    }
  }, [preloadedCities]);

  useEffect(() => {
    setLoading(false);
  }, [preloadedStores, preloadedSearch, preloadedCities]);

  const handleViewInMap = (latitude, longitude) => {
    if (!isShowingMap) {
      setShowingMap(true);
    }

    setCenteredStore([latitude, longitude]);
  };

  const filterStores = (storeItem) => {
    if (filteredCity === false || filteredCity === null || filteredCity === undefined) return true;
    if (!storeItem.latitude || !storeItem.longitude) return false;
    return storeItem.cityName.toLowerCase() === filteredCity.toLowerCase();
  };

  useEffect(() => {
    if (filteredCity !== null) {
      const newStoresFiltered = stores.filter(filterStores);
      if (newStoresFiltered.length > 0) {
        const firstStore = newStoresFiltered[0];
        if (firstStore.latitude && firstStore.longitude) {
          setCenteredStore([firstStore.latitude, firstStore.longitude]);
        }
      }
      setLoading(false);
    }
  }, [filteredCity]);

  const handleClickOnMapStore = (coords) => {
    const markedStore = stores.filter((store) => store.latitude === coords[0] && store.longitude === coords[1]).shift();
    dataLayerHandleEvent({
      event: 'store_map',
      event_category: 'store',
      event_detail_1: markedStore?.id || '',
      event_detail_2: markedStore?.name?.toLowerCase() || '',
    });
    if (showingFullMap) {
      setShowingFullMap(false);
    }
    setSelectedStore({ ...markedStore, showingStoreImg: false });
    setCenteredStore(coords);
    setColoredStore(markedStore.id);
    const newColoredStore = document.querySelectorAll('#selectedStore')[0];

    if (newColoredStore !== undefined) {
      newColoredStore.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  };

  const breadcrumbFragments = [
    {
      title: t('generico', 'breadcrumb.home', '**Home'),
      href: `/${locale}`,
    },
    {
      title: 'Shops',
      href: `/${locale}/shops`,
    },
  ];

  // find country in allStoresByCountry by comparing country name normalized
  if (isCountry && currentCountry !== null) {
    const countryInAllStores = allStoresByCountry?.find((c) => {
      if (!c.country) return false;
      if (!currentCountry) return false;
      return removeStrangeCharacters(c.country.toLocaleLowerCase()) === removeStrangeCharacters(currentCountry.toLocaleLowerCase());
    });
    breadcrumbFragments.push({
      title: countryInAllStores?.country ?? currentCountry,
      href: countryInAllStores ? `/${countryInAllStores.url.toLocaleLowerCase()}` : normalizeUrl(`/${locale}/shops/${currentCountry.toLocaleLowerCase()}`),
    });

    if (filteredCity) {
      breadcrumbFragments.push({
        title: filteredCity,
        href: normalizeUrl(`/${locale}/shops/${currentCountry.toLocaleLowerCase()}/${filteredCity.toLocaleLowerCase()}`),
      });
    }
  } else if (currentCountry !== null && currentPlace !== null) {
    const countryInStoreUrl = preloadedStores?.[0]?.url?.split('/')?.[1];
    const currentCountryName = hasNonAlphaCharacters(currentCountry) && !!countryInStoreUrl ? countryInStoreUrl : currentCountry.toLocaleLowerCase();

    breadcrumbFragments.push(
      ...[
        {
          title: currentCountry,
          href: normalizeUrl(`/${locale}/shops/${currentCountryName.toLocaleLowerCase()}`),
        },
        {
          title: currentPlace,
          href: normalizeUrl(`/${locale}/shops/${currentCountryName.toLocaleLowerCase()}/${currentPlace.toLowerCase()}`),
        },
      ],
    );
  }

  const BannerComponent = ({ onClose }) => (
    <div className={styles.bookAppointment}>
      <a className={styles.appointmentClose} onClick={onClose}>
        <CloseMdSVG />
      </a>
      <div className={styles.appointmentHeader}>
        <p className={styles.appointmentTitle}>{t('tiendas.locator', 'book.appointment', 'Book your appointment')}</p>
        <p>{t('tiendas.locator', 'meet.a.member', 'Meet a member of our team foa a personalized shopping experience.')}</p>
      </div>
      <div className={styles.appointmentItem}>
        <VirtualAppointmentSVG />
        <div>
          <p className={styles.appointmentItemTitle}>{t('tiendas.locator', 'virtual.appointment', 'Virtual appointment')}</p>
          <p>
            {t(
              'tiendas.locator',
              'member.guide',
              'A member of our team will guide you through our products and the store, from the comfort of your home and any purchases you make delivered to your door.',
            )}
          </p>
        </div>
      </div>
      <div className={styles.appointmentItem}>
        <InStoreSVG />

        <div>
          <p className={styles.appointmentItemTitle}>{t('tiendas.locator', 'in.store.appointment', 'In store appointment')}</p>
          <p>
            {t(
              'tiendas.locator',
              'member.welcome',
              'A store member will welcome you at the store with the proper safety measures to guide you through your shopping experience.',
            )}
          </p>
        </div>
      </div>
    </div>
  );

  BannerComponent.propTypes = {
    onClose: func,
  }; /* PASARLO A SU PROPIO COMPONENTE */

  let searchPlace = null;
  if (placeProp || countryProp) {
    searchPlace = placeProp || countryProp;
  }
  const pageTitle =
    searchPlace ?
      t('tiendas.locator', `stores.in.${placeProp ? 'city' : 'country'}`, 'Tiendas de Zapatos Camper en <PLACE>').replace('<PLACE>', searchPlace)
    : 'Camper';
  const MapComponentMemo = useMemo(
    () => (
      <Map
        sticky={!isMobile}
        center={centeredStore}
        visible={isShowingMap}
        viewStoreInMap={false}
        pins={stores.filter(filterStores).map(({ latitude, longitude }) => ({
          latitude,
          longitude,
        }))}
        zoom={centeredStore ? 15 : 16}
        onClickMarker={handleClickOnMapStore}
        fitToBounds={centeredStore === null}
        isMobile={isMobile}
        className={styles.map}
        onZoom={null}
      />
    ),
    [isMobile, centeredStore, isShowingMap, stores, filteredCity],
  );

  const handleChangeCountry = async (value) => {
    setLoadingCities(true);
    setSelectedCountry(value);
  };

  const handleFindStoresBySelect = async () => {
    setLoading(true);
    isSearchingFromNotFound.current = true;
    setSearchString(selectedCity || selectedCountry?.value);
  };

  useEffect(() => {
    const getRegionsHandler = async () => {
      if (selectedCountry !== null) {
        try {
          const { id } = selectedCountry;
          const result = await getRegionsAndCities(`${lang}_${id}`);
          const { cities: newCities } = result;

          setSelectedCity(null);

          if (newCities?.length > 0) {
            setFilteredCities(newCities);
            setCountryHasBadData(false);
          } else {
            setFilteredCities([]);
            setCountryHasBadData(true);
          }

          setLoadingCities(false);
        } catch (e) {
          setLoadingCities(false);
        }
      }
    };

    getRegionsHandler();
  }, [selectedCountry]);

  const handleFindStoresByCountry = async () => {
    dataLayerHandleEvent({
      event: 'store_personal_shopper',
      event_category: 'store',
      event_detail_1: '',
      event_detail_2: '',
    });
    if (!detectedCountryName) {
      const country = countries.filter((countryObject) => countryObject.id === market);
      let resultByCountry = null;
      if (country && country.length) {
        try {
          resultByCountry = await getStoresByCountry(null, locale, country[0].name, false);
        } catch (error) {
          console.error('error', error);
          Modal.error({
            title: t('tiendas.locator', 'provider.error', '**The geocoding provider returned an error.'),
            content: '',
          });
        }
        if (resultByCountry && 'stores' in resultByCountry && resultByCountry.stores !== undefined) {
          setAppointmentVisible(true);
          setCountryName(country[0].name);
          setStoresByCountry(resultByCountry);
        }
      } else {
        Modal.error({
          title: t('tiendas.locator', 'provider.error', '**The geocoding provider returned an error.'),
          content: '',
        });
      }
    } else {
      setAppointmentVisible(true);
    }
  };

  const AppointmentBtn = ({ tablet }) => {
    if (COUNTRIES_NO_PERSONAL_SHOPPER.includes(market) || !stores.some((s) => s.bookingAvailability)) {
      return null;
    }
    return (
      <button className={`${styles.bookAppointmentBtn} ${tablet ? styles.bookAppointmentBtnTablet : ''}`} onClick={handleFindStoresByCountry}>
        <img src="/assets-new/img/shops/store-appointment.png" alt="store-icon" />
        {t('tiendas.locator', 'banner.title', 'Book your appointment')}
        <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
      </button>
    );
  };

  AppointmentBtn.propTypes = {
    tablet: bool,
  };

  const onMouseOverStore = (item) => {
    setSelectedStore({ ...item, showingStoreImg: true });
    setColoredStore(item.id);
    dataLayerHandleEvent({
      event: 'store_map',
      event_category: 'store',
      event_detail_1: item?.id || '',
      event_detail_2: item?.name?.toLowerCase() || '',
    });
  };
  const centerMap = (item) => {
    setCenteredStore([item.latitude, item.longitude]);
  };

  const debouncedCenterMap = debounce(centerMap, 500);

  const onClickStoreHref = (id, name) => {
    dataLayerHandleEvent({
      event: 'store_details',
      event_category: 'store',
      event_detail_1: id,
      event_detail_2: name?.toLowerCase() || '',
    });
  };

  const renderSelectedStoreFloatingCard = () => (
    <div className={styles.floatingStoreCard}>
      <StoreTypeLabel store={selectedStore} t={t} />
      <p className={styles.storeName}>{selectedStore.name}</p>
      <p
        className={styles.storeAddress}
      >{`${selectedStore.address1}, ${selectedStore.postalCode}, ${selectedStore.regionName}, ${selectedStore.countryCode}`}</p>
      {selectedStore.status && selectedStore.statusText ?
        <StoreStatus status={selectedStore.status} statusText={selectedStore.statusText} />
      : null}
      {selectedStore.storeHours?.length && <Scheduler schedule={selectedStore.storeHours} storeName={selectedStore.name} storeId={selectedStore.id} />}
      {selectedStore.urlGoogleMaps && (
        <>
          <a className={styles.urlGoogleMaps} href={selectedStore.urlGoogleMaps} rel="noreferrer" target="_blank">
            {t('tiendas.locator', 'get.diretions', 'Get directions')}
            <img src="/assets-new/img/shops/chevron-right.png" alt="chevron-right" />
          </a>
          <br></br>
        </>
      )}
      <a
        onClick={() => onClickStoreHref(selectedStore.id, selectedStore.name)}
        className={`${styles.detailsLink}`}
        href={`/${lang}_${market}/shops${selectedStore.url}`}
      >
        {t('tiendas.locator', 'see.details', '***See details')}
      </a>
    </div>
  );

  const NotFoundMemo = useMemo(
    () =>
      searchedOne.current && (
        <div className={styles.noResults}>
          <b>
            {t('tiendas.locator', 'no.results', 'Sorry, but no results were found that match your search:')} {`"${searchString}"`}
          </b>
          <br />
          <p>{t('tiendas.locator', 'choose.country.or.city', '**Choose your country and/or city to find a store')}</p>
          <div className={styles.selectWrapper}>
            <AutoComplete
              onSelectProp={(selected) => {
                handleChangeCountry(selected);
                notFoundCountryRef.current.blur();
              }}
              renderCustomInput={
                <Input
                  ref={notFoundCountryRef}
                  suffix={<ChevronSVG className={styles.autoCompleteChevron} />}
                  placeholder={t('modal.idiomas', 'select.country', 'Select a country')}
                  className={styles.autoCompleteInput}
                  // @ts-ignore !antd
                  autoComplete={false}
                  value={selectedCountry !== null ? selectedCountry.name : ''}
                />
              }
              inputValue={selectedCountry !== null ? selectedCountry.name : ''}
              id="country-input"
              baseOptions={countries ? countries.map((item) => ({ value: item.name, id: item.id })) : []}
              dropdownClassName={styles.dropdown}
            />
            {/* @ts-ignore Antd!! */}
            <Spin spinning={loadingCities}>
              <AutoComplete
                onSelectProp={(selected) => {
                  setSelectedCity(selected.value);
                  notFoundCityRef.current.blur();
                }}
                renderCustomInput={
                  <Input
                    ref={notFoundCityRef}
                    suffix={<ChevronSVG className={styles.autoCompleteChevron} />}
                    className={styles.autoCompleteInput}
                    // @ts-ignore !antd
                    autoComplete={false}
                    value={selectedCity !== null ? selectedCity : ''}
                  />
                }
                inputValue={selectedCity}
                id="city-input"
                baseOptions={filteredCities !== null ? filteredCities.map((item) => ({ value: item })) : []}
                dropdownClassName={styles.dropdown}
                disabled={filteredCities === null}
              />
            </Spin>
          </div>
          <br />
          <div className={styles.findStoresButtonWrapper}>
            <Button disabled={countryHasBadData} onClick={handleFindStoresBySelect} className={`${styles.locationButton} ${styles.firstLocation}`}>
              {t('tiendas.locator', 'find.stores', '**Find Stores')}
            </Button>
          </div>
          <br />
          <div className={styles.geoButtonWrapper}>
            <p className={styles.geoButtonTitle}>
              {t('tiendas.locator', 'or', '**Or')}
              <span className="lowercase"> {t('tiendas.locator', 'activate.geolocation', 'Activate your geolocation to see nearest stores:')}</span>
            </p>
            <Button icon={<LocationSVG height={26} width={26} />} onClick={handleGetLocation} className={`${styles.locationButton} ${styles.firstLocation}`}>
              {t('tiendas.locator', 'use.location', 'Use my location')}
            </Button>
          </div>
        </div>
      ),
    [stores, selectedCountry, selectedCity, loadingCities, filteredCities, countryHasBadData],
  );

  const mapClassName = showingFullMap ? styles.mapContainer : styles.listMapContainer;
  const SecondStep = (
    <>
      <div className={`${styles.searchPanelFloating} ${!showingFullMap ? styles.searchPanelFloatingVisible : ''}`}>
        <div className={styles.inputAndFilter}>
          <div className={styles.storeListHeadMobile}>
            <div className={styles.storeSearch}>
              <Input.Search
                placeholder={t('tiendas.locator', 'search.store')}
                value={searchString}
                onChange={(e) => handleSearchChange(e)}
                onSearch={handleSearch}
                className={styles.searchInput}
                autoFocus
              />
            </div>
            <FilterPanel filterBy={filterBy} filterByOpen={filterByOpen} onClickFilter={onClickFilter} setFilterByOpen={setFilterByOpen} />
          </div>
        </div>
        <div className={styles.totalStores}>
          <span className={styles.storesCount}>
            {stores.length} {t('tiendas.locator', 'stores', 'Stores')}
          </span>
        </div>
      </div>
      {/* @ts-ignore antd!! */}
      <Spin spinning={loading}>
        <div className={styles.mainContent}>
          {countryCode !== 'JP' && showingFullMap && <AppointmentBtn tablet={true} />}
          <div className={`${styles.searchPanel} ${!showingFullMap ? styles.searchPanelStep2 : ''}`}>
            <div className={styles.storeSearch}>
              <Input.Search
                placeholder={t('tiendas.locator', 'search.store')}
                value={searchString}
                onChange={(e) => handleSearchChange(e)}
                onSearch={handleSearch}
                className={styles.searchInput}
                autoFocus
              />
            </div>
            <div className={styles.geoButtonWrapper}>
              <Button icon={<LocationSVG height={26} width={26} />} onClick={handleGetLocation} className={`${styles.locationButton} ${styles.firstLocation}`}>
                {t('tiendas.locator', 'use.location', 'Use my location')}
              </Button>
            </div>
            {countryCode !== 'JP' && <AppointmentBtn />}
          </div>
          {stores.length === 0 && (
            <div className={styles.imagePanel}>
              <img src="/assets-new/img/shops/camper-shop.jpg" alt="Camper shop" />
            </div>
          )}
          {stores.length > 0 ?
            <>
              {!showingFullMap && (
                <div className={styles.content}>
                  {(placeProp || countryProp) && !searchedOne.current && <h1 className={styles.storesInCity}>{pageTitle}</h1>}
                  <div className={placeProp || countryProp ? `${styles.storeListHead} ${styles.storeListHeadWithCity}` : styles.storeListHead}>
                    <div>
                      <span className={styles.storesCount}>
                        {stores.length} {t('tiendas.locator', 'stores', 'Stores')}
                      </span>
                    </div>
                    <div className={!filterByOpen ? styles.filterBy : `${styles.filterBy} ${styles.open}`}>
                      <span onClick={() => setFilterByOpen(!filterByOpen)}>
                        {t('tiendas.locator', 'filter.by', 'Filter by')}{' '}
                        {!filterByOpen ?
                          <ChevronSVG />
                        : <CloseMdSVG width={16} height={16} />}
                      </span>
                      {filterByOpen && (
                        <>
                          <p className={styles.collection}>{t('tiendas.locator', 'collections', 'Collections')}</p>
                          <ul>
                            <li>
                              <Checkbox checked={filterBy.includes('featWomen')} onChange={() => onClickFilter('featWomen')}>
                                {t('tiendas.locator', 'women.collection', 'Women Collection')}
                              </Checkbox>
                            </li>
                            <li>
                              <Checkbox checked={filterBy.includes('featMen')} onChange={() => onClickFilter('featMen')}>
                                {t('tiendas.locator', 'men.collection', 'Men Collection')}
                              </Checkbox>
                            </li>
                            <li>
                              <Checkbox checked={filterBy.includes('featKids')} onChange={() => onClickFilter('featKids')}>
                                {t('tiendas.locator', 'kids.collection', 'Kids Collection')}
                              </Checkbox>
                            </li>
                            <li>
                              <Checkbox checked={filterBy.includes('featBags')} onChange={() => onClickFilter('featBags')}>
                                {t('tiendas.locator', 'bag.collection', 'Bags & Accesories Collection')}
                              </Checkbox>
                            </li>
                            <li>
                              <Checkbox checked={filterBy.includes('camperLab')} onChange={() => onClickFilter('camperLab')}>
                                {t('tiendas.locator', 'camperlab.collection', 'Camperlab Collection')}
                              </Checkbox>
                            </li>
                          </ul>
                          <p className={styles.collection}>{t('tiendas.locator', 'services', 'Services')}</p>
                          <ul>
                            <li>
                              <Checkbox checked={filterBy.includes('takeback')} onChange={() => onClickFilter('takeback')}>
                                {t('tiendas.locator', 'takeback.service', 'Take Back')}
                              </Checkbox>
                            </li>
                          </ul>
                          <p className={styles.collection}>{t('tiendas.locator', 'store.type', 'Store type')}</p>
                          <ul>
                            {STORE_TYPES.map((store) => (
                              <li key={store.type}>
                                <Checkbox checked={filterBy.includes(`tipologia: ${store.type}`)} onChange={() => onClickFilter(`tipologia: ${store.type}`)}>
                                  {t(...store.label)}
                                </Checkbox>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                  <StoresGrid
                    stores={sortStoresByTypology(stores.filter(filterStores))}
                    withMap={isShowingMap}
                    handleViewInMap={handleViewInMap}
                    onClickStoreHref={onClickStoreHref}
                    selector={null}
                    coloredStore={coloredStore}
                    onMouseOverStore={(item) => {
                      onMouseOverStore({ ...item, showingStoreImg: true });
                      debouncedCenterMap(item);
                    }}
                    selectedStoreId={selectedStore ? selectedStore.id : null}
                  />
                </div>
              )}
              <div
                className={`${isShowingMap ? mapClassName : `${mapClassName} ${styles.mapMobileHidden}`} ${isCountry && cities && isShowingMap ? styles.listMapCountry : ''}`}
              >
                {selectedStore && selectedStore.imageMain && selectedStore.showingStoreImg && (
                  <div className={styles.returnToMap} onClick={() => setSelectedStore({ ...selectedStore, showingStoreImg: false })}>
                    <span>
                      {t('tiendas.locator', 'return.to.map', 'Return to map')} <CloseSVG />
                    </span>
                  </div>
                )}
                {selectedStore && selectedStore.imageMain && !selectedStore.showingStoreImg && (
                  <div className={styles.returnToMap} onClick={() => setSelectedStore({ ...selectedStore, showingStoreImg: true })}>
                    <span>{t('tiendas.locator', 'return.to.store', 'Return to store')}</span>
                  </div>
                )}
                {selectedStore && selectedStore.imageMain && selectedStore.showingStoreImg && (
                  <img className={styles.imageStoreCover} src={selectedStore.imageMain} alt={`Store ${selectedStore.name}`} />
                )}
                {MapComponentMemo}
                {selectedStore && renderSelectedStoreFloatingCard()}
              </div>
            </>
          : NotFoundMemo}
        </div>
      </Spin>
      {loading && stores.length === 0 && <div className={styles.spacer} />}
    </>
  );

  return (
    <Layout
      {...defaultProps}
      breadcrumbs={false}
      metatags={{ titlePage: pageTitle }}
      pageName={'Shoplocator'}
      extratags={
        urlCanonical ?
          [
            <link key="canonical" rel="canonical" href={`${baseUrl}/${locale}${urlCanonical}`} />,
            <script
              key="scroll-restoration"
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: 'history.scrollRestoration = "manual"',
              }}
            />,
          ]
        : [
            <script
              key="scroll-restoration"
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: 'history.scrollRestoration = "manual"',
              }}
            />,
          ]
      }
      gtmProps={{ url: urlCanonical }}
    >
      <div className={styles.container}>{SecondStep}</div>
      <div className={styles.breadcrumbs}>
        <Breadcrumb
          locale={locale}
          useNextLink={false}
          shallow={false}
          onClick={(name) => {
            if (name === 'shops') {
              restart();
            }
            setLoading(true);
          }}
          fragments={breadcrumbFragments}
          withLdJson={true}
        />
      </div>
      {shoppingLinks?.length > 0 && (
        <div className={styles.keepShoppingDiv}>
          <h2>{t('tiendas.locator', 'keep.shopping', 'Keep shopping online?')}</h2>
          <div className={styles.linksDiv}>
            {shoppingLinks?.map((menu) => (
              <a
                key={menu?.title}
                href={menu?.slug || `${baseUrl}/${locale}`}
                data-tracking={menu?.tracking?.replace('main-menu', 'shop-locator')}
                data-trackingga4={menu?.trackingGA4?.replace('main-menu', 'shop-locator')}
              >
                {menu?.title?.toLowerCase() !== 'CAMPERLAB' ? menu?.title : 'CAMPERLAB'}
              </a>
            ))}
          </div>
        </div>
      )}
      {allStoresByCountry && allStoresByCountry.length > 0 && (
        <div className={styles.keepShoppingDiv}>
          <h2>{t('tiendas.locator', 'stores.by.country', 'Camper stores by country')}</h2>
          <div className={`${styles.linksDivCountries} ${styles.storesByCountry}`}>
            {allStoresByCountry
              .sort((a, b) => a.country.localeCompare(b.country))
              .map((store) => (
                <a key={store.country} href={normalizeUrl(`/${store.url}`)}>
                  {store?.country}
                </a>
              ))}
          </div>
        </div>
      )}
      {allStoresByCity && allStoresByCity.length > 0 && (
        <div className={styles.keepShoppingDiv}>
          <h2>{`${t('tiendas.locator', 'stores.in.country', 'Camper stores in ').replace('<PLACE>', searchPlace.replace('spana', 'spaña')).replace('<CITY>', searchPlace.replace('spana', 'spaña'))}`}</h2>
          <div className={`${styles.linksDivCountries} ${styles.storesByCountry}`}>
            {allStoresByCity
              .sort((a, b) => a.city.localeCompare(b.city))
              .map((store) => (
                <a key={store?.city} href={normalizeUrl(`/${store?.url}`)}>
                  {store?.city.toLocaleLowerCase()}
                </a>
              ))}
          </div>
        </div>
      )}
      <AppointmentModal visible={appointmentVisible} onClose={() => setAppointmentVisible(null)} store={{}} storesByCountry={storesByCountry} />
    </Layout>
  );
}
